import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../config/api";
import "./SpecialScreeningBulkBooking.css";

// ─────────────────────────────────────────────────────────────────────────
// Special Screening & Bulk Booking Page
//
// All content, images and section ordering are driven by the admin CMS
// through the generic `cms_page_configs` + `cms_page_sections` tables,
// exactly the same way as the Advertise With Us and Mamangam Hall
// Booking pages. The page_key is `special-screening-bulk-booking`.
//
// Sections rendered (in display_order):
//   1. Inner banner with breadcrumb (heroTitle / heroSubtitle / heroDescription)
//   2. Full banner image
//   3. "Special Screenings" (what_we_do) – centred heading + 8 background-image
//      cards (first card wider, remaining 7 equal).
//   4. "Premium Package" (premium_package) – gradient banner with WhatsApp CTA
//      ─ CTA opens https://wa.me/<number>?text=<prefilled> using the
//        whatsapp_number + whatsapp_prefilled_text fields from the CMS
//      ─ items 3 & 4 share the same outer .what-we-do.style-2 wrapper
//   5. "Bulk Booking" intro (bulk_booking_intro) – left text + right image
//   6. "Ideal For" (icon_grid) – 6 background-image cards inside the same
//      bulk-booking-sec wrapper
//   7. "Benefits" (benefit_list) – 5 icon-title cards inside benafic-col
//      (no check icon, matches reference HTML)
//      ─ items 5, 6 & 7 share the same outer .bulk-booking-sec wrapper
//
// Card items for Special Screenings / Ideal For use either `image_url` (new
// shape) or `icon` (legacy fallback) plus `title` and `description`. Admin
// `description` field is optional – only rendered when present.
//
// There are no hard-coded fallbacks — if the API is unreachable or no
// page config exists the page simply shows a loading spinner and then
// an empty state, so the admin team has full control.
// ─────────────────────────────────────────────────────────────────────────

const PAGE_KEY = "special-screening-bulk-booking";

// Inline SVGs reused from the original HTML so we don't have to
// re-create them or add any extra files.
const HomeIconSvg = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g clipPath="url(#spl_clip_home)">
      <path
        d="M11.0833 13.9997H2.91667C1.30842 13.9997 0 12.6913 0 11.083V5.67203C0 4.70078 0.480667 3.79661 1.28567 3.25411L5.36842 0.498445C6.3595 -0.170055 7.6405 -0.170055 8.63158 0.498445L12.7149 3.25411C13.5193 3.79661 14 4.7002 14 5.67203V11.083C14 12.6913 12.6916 13.9997 11.0833 13.9997ZM7 1.16461C6.65933 1.16461 6.31867 1.26495 6.02117 1.4662L1.93783 4.22128C1.45483 4.54678 1.16667 5.0887 1.16667 5.67145V11.0824C1.16667 12.0473 1.95183 12.8324 2.91667 12.8324H11.0833C12.0482 12.8324 12.8333 12.0473 12.8333 11.0824V5.67203C12.8333 5.08928 12.5452 4.54678 12.0627 4.22186L7.97883 1.4662C7.68133 1.26495 7.34067 1.16461 7 1.16461Z"
        fill="#6E727B"
      />
    </g>
    <defs>
      <clipPath id="spl_clip_home">
        <rect width="14" height="14" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 12L10 8L6 4"
      stroke="#6E727B"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Small star icon used in the regular section tag pills (indigo pill on lavender)
const TagStarSvg = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7 0L8.7318 4.53659L13.1144 4.53659L9.45385 7.3209L10.9039 11.8571L7 9.35532L3.09611 11.8571L4.54615 7.3209L0.885602 4.53659L5.2682 4.53659L7 0Z"
      fill="#2e3192"
    />
  </svg>
);

// Hook that fetches the page config + sections from the CMS API.
const useCmsPage = (pageKey) => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPage = async () => {
      try {
        setLoading(true);
        const data = await api.getPageConfigByKey(pageKey);
        if (cancelled) return;
        if (data && data.id) {
          setPageData(data);
        } else {
          setError("No data received from API");
          setPageData(null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch Special Screening page config:", err);
        setError(err.message || "Failed to load page content");
        setPageData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPage();
    return () => {
      cancelled = true;
    };
  }, [pageKey]);

  return { pageData, loading, error };
};

// Look up a section by its `section_type` in the API data, then sort the
// active ones by display_order so the page renders sections in the right
// order regardless of insertion order.
const pickSections = (pageData) => {
  if (!pageData || !Array.isArray(pageData.sections)) {
    return {
      fullBanner: null,
      specialScreenings: null,
      premiumPackage: null,
      bulkBooking: null,
      idealFor: null,
      benefits: null,
    };
  }

  const active = pageData.sections
    .filter((s) => s.is_active)
    .sort((a, b) => a.display_order - b.display_order);

  const find = (type) => active.find((s) => s.data?.section_type === type);

  return {
    fullBanner: find("full_banner"),
    specialScreenings: find("what_we_do"),
    premiumPackage: find("premium_package"),
    bulkBooking: find("bulk_booking_intro"),
    idealFor: find("icon_grid"),
    benefits: find("benefit_list"),
  };
};

const SpecialScreeningBulkBooking = () => {
  const { pageData, loading, error } = useCmsPage(PAGE_KEY);
  const sections = useMemo(() => pickSections(pageData), [pageData]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Unable to load page content</h4>
          <p className="mb-0">
            {error ||
              "No data available. Please configure the page in the admin panel."}
          </p>
        </div>
      </div>
    );
  }

  const heroTitle = pageData.title || "Special Screening & Bulk Booking";
  const heroSubtitle = pageData.subtitle || "Events";
  // hero description is rich-text HTML; only render if present
  const heroDescriptionHtml = pageData.description || "";

  // Whether each "group" has any content – used to decide which wrappers to render
  const hasWhatWeDoGroup =
    !!sections.specialScreenings?.data || !!sections.premiumPackage?.data;
  const hasBulkBookingGroup =
    !!sections.bulkBooking?.data ||
    !!sections.idealFor?.data ||
    !!sections.benefits?.data;

  return (
    <div className="spl-page">
      {/* Inner Banner / Breadcrumb */}
      <div className="inner-banner">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <ul className="breadcrumps">
                <li>
                  <Link to="/">
                    <HomeIconSvg />
                    {heroSubtitle}
                  </Link>
                </li>
                <li>
                  <ChevronRight />
                </li>
                <li className="breadcrumps-current">{heroTitle}</li>
              </ul>
              {heroDescriptionHtml && (
                <div
                  className="spl-hero-description"
                  dangerouslySetInnerHTML={{ __html: heroDescriptionHtml }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full Banner */}
      {sections.fullBanner?.data?.image_url && (
        <div className="full-banner">
          <img
            src={sections.fullBanner.data.image_url}
            alt={heroTitle}
            className="w-100"
          />
        </div>
      )}

      {/* Combined: What We Do (cards) + Premium Package (gradient banner)
          ─ both live inside the same .what-we-do.style-2 wrapper on the
          original design, so the wrapper is only rendered when at least
          one of them has content. */}
      {hasWhatWeDoGroup && (
        <div
          className="what-we-do style-2"
          style={{ "--film-bg": "url(/assets/img/film-bg-element.png)" }}
        >
          <div className="container">
            <div className="row">
              {sections.specialScreenings?.data && (
                <SpecialScreeningsInner section={sections.specialScreenings} />
              )}
              {sections.premiumPackage?.data && (
                <PremiumPackageInner section={sections.premiumPackage} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Combined: Bulk Booking (intro) + Ideal For + Benefits
          ─ all three live inside the same .bulk-booking-sec wrapper on
          the original design, so the wrapper is only rendered when at
          least one of them has content. */}
      {hasBulkBookingGroup && (
        <div
          className="bulk-booking-sec"
          style={{ backgroundImage: "url(/assets/img/bg-rainbow.png)" }}
        >
          <div className="container">
            {sections.bulkBooking?.data && (
              <BulkBookingIntroInner section={sections.bulkBooking} />
            )}
            {sections.idealFor?.data && (
              <IdealForInner section={sections.idealFor} />
            )}
            {sections.benefits?.data && (
              <BenefitsInner section={sections.benefits} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Builds the wa.me link exactly the same way Advertise With Us and
// Mamangam Hall Booking do. Strips non-digits (except a leading +) from
// the number and URL-encodes the prefilled text.
const buildWhatsAppLink = (number, prefilledText) => {
  if (!number) return "#";
  const cleanNumber = String(number).replace(/[^\d+]/g, "").replace(/^\+/, "");
  const encodedText = encodeURIComponent(prefilledText || "");
  return `https://wa.me/${cleanNumber}${encodedText ? `?text=${encodedText}` : ""}`;
};

// ── Inner: What We Do (centred heading + background-image card grid) ─────
// Reference spl-booking.html renders the Special Screenings as 8
// .what-bg cards (full background image + dark gradient overlay + white
// title + description pinned to the bottom). The first card uses
// col-lg-8 (large) and the remaining 7 use col-lg-4 (equal halves).
const SpecialScreeningsInner = ({ section }) => {
  const data = section.data || {};
  const items = Array.isArray(data.items) ? data.items : [];
  if (!data.tag && !data.heading && items.length === 0) return null;

  return (
    <>
      <div className="col-md-12 text-center">
        {data.tag && (
          <div className="tag align-items-center gap-2 mb-3">
            <TagStarSvg />
            {data.tag}
          </div>
        )}
        {data.heading && (
          <h2 className="text-center mb-3 main-head">{data.heading}</h2>
        )}
        {data.sub_heading && (
          <p className="text-center mb-5 sub-head">{data.sub_heading}</p>
        )}
      </div>

      {items.map((item, idx) => {
        const image = item.image_url || item.icon || "";
        const isFirst = idx === 0;
        const colClass = isFirst
          ? "col-lg-8 col-md-6"
          : "col-lg-4 col-md-6";
        return (
          <div key={idx} className={colClass}>
            {image ? (
              <div
                className="what-bg"
                style={{ backgroundImage: `url(${image})` }}
              >
                <div className="what-container">
                  {item.title && <h4>{item.title}</h4>}
                  {item.description && <p>{item.description}</p>}
                </div>
              </div>
            ) : (
              <div className="what-bg">
                <div className="what-container">
                  {item.title && <h4>{item.title}</h4>}
                  {item.description && <p>{item.description}</p>}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

// ── Inner: Premium Package (gradient banner with WhatsApp CTA) ────────────
// Returns only the col-lg-12 wrapper + .pre-pkg child that goes INSIDE
// the .row of .what-we-do.style-2. The CTA opens WhatsApp with the
// configured number + pre-filled message (same as Advertise With Us and
// Mamangam Hall Booking). If no WhatsApp number is configured yet, the
// CTA falls back to a non-functional button so the section still renders.
const PremiumPackageInner = ({ section }) => {
  const data = section.data || {};
  if (!data.tag && !data.content && !data.button_text) return null;

  const whatsappUrl = buildWhatsAppLink(
    data.whatsapp_number,
    data.whatsapp_prefilled_text,
  );
  const useWhatsAppLink = !!data.whatsapp_number;

  let ctaElement = null;
  if (data.button_text) {
    if (useWhatsAppLink) {
      ctaElement = (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-style-2"
        >
          {data.button_text}
        </a>
      );
    } else {
      ctaElement = (
        <button type="button" className="btn-style-2">
          {data.button_text}
        </button>
      );
    }
  }

  return (
    <div className="col-lg-12">
      <div
        className="pre-pkg"
        style={{
          backgroundImage: data.background_image_url
            ? `url(${data.background_image_url})`
            : "url(/assets/img/premium-area-bg.png)",
        }}
      >
        <div>
          {data.tag && (
            <div className="tag align-items-center gap-2">
              <img src="/assets/img/ai-star.svg" alt="" />
              {data.tag}
            </div>
          )}
          {data.content && (
            <p dangerouslySetInnerHTML={{ __html: data.content }} />
          )}
        </div>
        {ctaElement}
      </div>
    </div>
  );
};

// ── Inner: Bulk Booking intro (left text + right image) ─────────────────
// Returns only the .row.align-items-center block – sits inside the
// outer .container of .bulk-booking-sec.
const BulkBookingIntroInner = ({ section }) => {
  const data = section.data || {};
  if (!data.tag && !data.heading && !data.description && !data.side_image_url) {
    return null;
  }

  return (
    <div className="row align-items-center">
      <div className="col-lg-6">
        {data.tag && (
          <div className="tag align-items-center gap-2 mb-3">
            <TagStarSvg /> {data.tag}
          </div>
        )}
        {data.heading && <h4 className="main-head mb-4">{data.heading}</h4>}
        {data.description && (
          <p
            className="sub-head"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
        )}
      </div>
      {data.side_image_url && (
        <div className="col-lg-6">
          <figure>
            <img
              className="img-fluid w-100"
              src={data.side_image_url}
              alt={data.heading || "Bulk Booking"}
            />
          </figure>
        </div>
      )}
    </div>
  );
};

// ── Inner: Ideal For (title row + background-image card grid) ────────────
// Returns two .row blocks that go inside the outer .container of
// .bulk-booking-sec. Cards use the same .what-bg / .what-container style
// as Special Screenings (matches reference spl-booking.html).
const IdealForInner = ({ section }) => {
  const data = section.data || {};
  const items = Array.isArray(data.items) ? data.items : [];
  if (items.length === 0 && !data.heading) return null;

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          {data.heading && <h4 className="sub-head mb-3">{data.heading}</h4>}
        </div>
      </div>
      <div className="row">
        {items.map((item, idx) => {
          const image = item.image_url || item.icon || "";
          return (
            <div key={idx} className="col-lg-4 col-md-6">
              {image ? (
                <div
                  className="what-bg"
                  style={{ backgroundImage: `url(${image})` }}
                >
                  <div className="what-container">
                    {item.title && <h4>{item.title}</h4>}
                    {item.description && <p>{item.description}</p>}
                  </div>
                </div>
              ) : (
                <div className="what-bg">
                  <div className="what-container">
                    {item.title && <h4>{item.title}</h4>}
                    {item.description && <p>{item.description}</p>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

// ── Inner: Benefits (title row + icon-list row) ─────────────────────────
// Returns two .row blocks that go inside the outer .container of
// .bulk-booking-sec (matches the original HTML structure).
// Reference HTML keeps the title <div class="col-md-12"> AND all the
// .col-lg-4 items as siblings INSIDE a single .row, and shows each
// benefit as a single icon + title inside .benafic-col (no check icon).
const BenefitsInner = ({ section }) => {
  const data = section.data || {};
  const items = Array.isArray(data.items) ? data.items : [];
  if (items.length === 0 && !data.heading) return null;

  return (
    <div className="row">
      <div className="col-md-12">
        {data.heading && <h4 className="sub-head mb-1">{data.heading}</h4>}
        {data.disclaimer && (
          <span className="tertiary-sub">{data.disclaimer}</span>
        )}
      </div>
      {items.map((item, idx) => (
        <div key={idx} className="col-lg-4 col-md-4 col-sm-6">
          <div className="benafic-col">
            <div className="benfit-data">
              {item.icon && <img src={item.icon} alt={item.title || ""} />}
              {item.title && <h4>{item.title}</h4>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SpecialScreeningBulkBooking;
